import React, {
  forwardRef,
  useId,
  useImperativeHandle,
  createContext,
  useContext,
  useCallback,
  useLayoutEffect,
  useEffect,
} from "react";
import isEqual from "react-fast-compare";
import { FaChevronDown } from "react-icons/fa";
import clsx from "clsx"; // <-- adicionado
import Chips from "../Chips/Chips";
import { useSelectField } from "./hooks/useSelectField";
import { useDropdownNavigation } from "./hooks/useDropdownNavigation";
import { useMergedSelectFieldState } from "./hooks/useMergedSelectFieldState";
import SearchBar from "./SearchBar";
import OptionItem from "./OptionItem";
import { FixedSizeList as List } from "react-window";
import Tooltip from "../Tooltip/Tooltip";

/**
 * SelectField - Componente de campo de seleção customizado, acessível e altamente configurável.
 *
 * Suporta seleção única ou múltipla, busca, renderização customizada de opções, mensagens de erro e sucesso, acessibilidade, integração com React Hook Form e internacionalização de mensagens.
 *
 * Principais recursos:
 * - Suporte a seleção única ou múltipla.
 * - Busca interna (searchable).
 * - Renderização customizada de opções via renderOption.
 * - Mensagens de erro, sucesso e texto auxiliar.
 * - Tooltip acessível no label (ícone "i"), com suporte a mouse, foco e teclado.
 * - Totalmente acessível: roles, aria-labels, aria-describedby, aria-live, aria-expanded, aria-controls, navegação por teclado.
 * - Suporte a temas claro e escuro.
 * - Internacionalização de mensagens via contexto ou prop.
 * - Compatível com React Hook Form.
 * - Testável via data-testid customizável.
 *
 * @param {SelectFieldProps} props Propriedades do componente.
 * @param {React.Ref<HTMLDivElement>} ref Referência para o container principal do SelectField.
 * @returns {JSX.Element} Elemento JSX do campo de seleção.
 *
 * @example
 * <SelectField
 *   label="Categoria"
 *   tooltip="Selecione uma ou mais categorias. Use a busca para filtrar."
 *   options={[{ value: "a", label: "A" }, { value: "b", label: "B" }]
 *   value={value}
 *   onChange={setValue}
 *   multiple
 *   searchable
 *   error={!!error}
 *   errorMessage={error}
 *   required
 * />
 *
 * @see SelectFieldProps para todas as propriedades disponíveis.
 *
 * @remarks
 * - O atributo `data-testid` do container segue a seguinte prioridade:
 *   1. `containerProps["data-testid"]`
 *   2. Valor padrão: `"select-field"`
 * - Tooltip: O ícone "i" só aparece se a prop `tooltip` for fornecida. O tooltip é acessível por mouse, foco e teclado (Enter/Espaço/Escape).
 * - Acessibilidade: O componente utiliza roles e atributos ARIA para garantir navegação e leitura por leitores de tela.
 * - Para melhor performance, funções passadas como props (ex: `onChange`, `onFocus`, `onBlur`, etc.)
 *   devem ser estáveis entre renders. Use sempre `useCallback` ao declará-las no componente pai.
 */

// Contexto de mensagens (pode ser compartilhado com InputField)
export interface SelectFieldI18nContext {
  getDefaultErrorMessage: (error?: string) => string;
  getDefaultSuccessMessage?: () => string;
}
export const SelectFieldMessagesContext = createContext<SelectFieldI18nContext>({
  getDefaultErrorMessage: () => "Seleção inválida",
});

// Tipos
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps {
  id?: string;
  options: SelectOption[];
  placeholder?: string;
  label?: string;
  error?: boolean;
  errorMessage?: string | ((ctx: { value: string }) => string);
  disabled?: boolean;
  helperText?: string;
  required?: boolean;
  theme?: "light" | "dark";
  searchable?: boolean;
  accessibilityLabel?: string;
  accessibilityErrorMessage?: string;
  successMessage?: string | ((ctx: { value: string }) => string);
  successIcon?: React.ReactNode;
  readOnly?: boolean;
  className?: string;
  inputClassName?: string;
  containerProps?: React.HTMLAttributes<HTMLDivElement> & { "data-testid"?: string };
  tabIndex?: number;
  i18nMessages?: SelectFieldI18nContext;
  renderOption?: (option: SelectOption, selected: boolean) => React.ReactNode;
  multiple?: boolean;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  maxSelected?: number;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
  dropdownFooter?: React.ReactNode;
  dropdownClassName?: string;
  optionClassName?: string;
  searchInputClassName?: string;
  tooltip?: string; // <-- Adicione esta linha
  showSuccess?: boolean; // <-- Adicione esta linha!
  children?: React.ReactNode; // <-- Add this line
  name?: string; // Adicione esta linha
  keyboardShortcuts?: Partial<Record<
    "ArrowDown" | "ArrowUp" | "Home" | "End" | "PageUp" | "PageDown" | "Enter" | " " | "Escape",
    (e: React.KeyboardEvent, ctx: { open: boolean; highlighted: number; options: SelectOption[] }) => void
  >>;
  compact?: boolean; // Adicione esta linha
}

// Novo componente Option
export const Option: React.FC<{ value: string; disabled?: boolean; children: React.ReactNode }> = () => null;

const DropdownListbox = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  (props, ref) => (
    <ul
      {...props}
      ref={ref}
      className={`dropdown-list${props.className ? " " + props.className : ""}`}
      role="listbox"
      tabIndex={0}
      data-testid={(props as Record<string, unknown>)["data-testid"] ?? "dropdown-listbox"}
    />
  )
);
DropdownListbox.displayName = "DropdownListbox";

const SelectField = forwardRef<HTMLDivElement, SelectFieldProps>((props, ref) => {
  const {
    id,
    placeholder = "Selecione uma opção",
    label,
    error = false,
    errorMessage,
    disabled = false,
    helperText,
    required = false,
    theme = "light",
    searchable = false,
    accessibilityLabel,
    accessibilityErrorMessage,
    successMessage,
    successIcon,
    readOnly = false,
    className,
    inputClassName,
    containerProps,
    tabIndex,
    i18nMessages,
    renderOption,
    multiple = false,
    value,
    onChange,
    maxSelected = Infinity,
    onFocus,
    onBlur,
    searchInputRef,
    dropdownFooter,
    dropdownClassName,
    optionClassName,
    searchInputClassName,
    name, // Adicione aqui
    compact = false, // Adicione esta linha
  } = props;

  // Contexto de mensagens
  const contextI18n = useContext(SelectFieldMessagesContext);
  const i18n = i18nMessages || contextI18n;

  // Ids
  const generatedId = useId();
  const selectId = id || generatedId;

  // Unifica estados relacionados em um hook customizado
  const {
    isDropdownOpen,
    setIsDropdownOpen,
    isFocused,
    setIsFocused,
    highlightedIndex,
    setHighlightedIndex,
    inputRef,
    dropdownRef,
    nativeSelectRef,
  } = useMergedSelectFieldState();

  // Hooks customizados
  const {
    searchQuery,
    setSearchQuery,
    showSuccess,
    filteredOptions,
    handleOptionSelect: rawHandleOptionSelect,
  } = useSelectField({
    ...props,
    closeDropdown: () => setIsDropdownOpen(false), // já está correto
  } as SelectFieldProps & { closeDropdown: () => void });

  // Corrija para garantir que sempre fecha no single-select
  const handleOptionSelect = useCallback(
    (optionValue: string) => {
      rawHandleOptionSelect(optionValue);
      if (!multiple) {
        setIsDropdownOpen(false);
      }
    },
    [rawHandleOptionSelect, multiple, setIsDropdownOpen]
  );

  // Navegação por teclado
  const handleKeyDown = useDropdownNavigation(
    isDropdownOpen,
    setIsDropdownOpen,
    filteredOptions,
    highlightedIndex,
    setHighlightedIndex,
    handleOptionSelect,
    multiple,
    value,
    onChange,
    searchQuery
  );

  // Mensagens
  const resolveMessage = useCallback(
    (
      msg: string | ((ctx: { value: string }) => string) | undefined,
      ctx: { value: string }
    ) => {
      if (typeof msg === "function") return msg(ctx);
      if (msg !== undefined) return msg;
      if (error) return i18n.getDefaultErrorMessage();
      if (props.showSuccess && i18n.getDefaultSuccessMessage)
        return i18n.getDefaultSuccessMessage();
      return undefined;
    },
    [error, props.showSuccess, i18n]
  );

  // NOVO: Suporte a composição via <Option>
  const composedOptions: SelectOption[] = [];
  function isOptionElement(child: React.ReactNode): child is React.ReactElement<{ value: string; disabled?: boolean; children: React.ReactNode }> {
    return (
      React.isValidElement(child) &&
      typeof child.type === "function" &&
      // @ts-expect-error: displayName is a static property
      (child.type.displayName === "Option")
    );
  }
  if (!props.options && props.children) {
    React.Children.forEach(props.children, child => {
      if (isOptionElement(child)) {
        composedOptions.push({
          value: child.props.value,
          label: typeof child.props.children === "string"
            ? child.props.children
            : child.props.children !== undefined
              ? React.Children.toArray(child.props.children).join("")
              : "",
          disabled: child.props.disabled,
        });
      }
    });
  }
  const finalOptions = props.options ?? composedOptions;

  // Valor exibido
  const selectedLabel = multiple
    ? (Array.isArray(value)
        ? finalOptions.filter(opt => value.includes(opt.value)).map(opt => opt.label).join(", ")
        : "")
    : finalOptions.find((option) => option.value === value)?.label
      // Se não encontrar label, converte value para string
      || (typeof value === "string" ? value : value != null ? String(value) : "");

  // Acessibilidade
  const valueString = Array.isArray(value) ? value.join(",") : value;
  const ariaLabel =
    error
      ? resolveMessage(accessibilityErrorMessage || errorMessage, { value: valueString }) ||
        "Seleção inválida"
      : accessibilityLabel || label || placeholder || "Campo de seleção";

  // Exponha ref externo usando callback ref para evitar leaks
  useImperativeHandle(ref, () => {
    if (typeof dropdownRef === "function") {
      return undefined as unknown as HTMLDivElement;
    }
    // Garante sempre HTMLDivElement
    return (dropdownRef as React.RefObject<HTMLDivElement>).current as HTMLDivElement;
  }, [dropdownRef]);

  // Chips
  const handleChipDelete = (id: string) => {
    if (!multiple || !Array.isArray(value)) return;
    onChange(value.filter(v => v !== id));
  };
  const handleClearAll = () => {
    if (multiple) onChange([]);
  };

  // Gerenciar tabulação correta
  const handleWrapperKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      setIsDropdownOpen(false);
      return;
    }
    if (e.key === "Escape") {
      setIsDropdownOpen(false);
      inputRef.current?.focus();
      return;
    }
    handleKeyDown(e as React.KeyboardEvent<HTMLDivElement>);
  };

  // Scroll para o item destacado
  useLayoutEffect(() => {
    if (!isDropdownOpen) return;
    const list = document.getElementById(`dropdown-listbox-${selectId}`);
    if (!list) return;
    const highlightedId =
      highlightedIndex >= 0 && filteredOptions[highlightedIndex]
        ? `option-${filteredOptions[highlightedIndex].value}`
        : undefined;
    if (highlightedId) {
      const el = document.getElementById(highlightedId);
      if (el && list) {
        const elTop = el.offsetTop;
        const elBottom = elTop + el.offsetHeight;
        if (elTop < list.scrollTop) {
          list.scrollTop = elTop;
        } else if (elBottom > list.scrollTop + list.clientHeight) {
          list.scrollTop = elBottom - list.clientHeight;
        }
      }
    }
  }, [isDropdownOpen, highlightedIndex, filteredOptions, selectId]);


  // Função para limitar seleção
  const handleLimitedOptionSelect = useCallback(
    (optionValue: string) => {
      if (!multiple) {
        handleOptionSelect(optionValue); // sempre fecha no single
      } else if (
        !Array.isArray(value) ||
        value.includes(optionValue) ||
        value.length < maxSelected
      ) {
        handleOptionSelect(optionValue);
      }
    },
    [multiple, value, maxSelected, handleOptionSelect]
  );

  // Centraliza renderização de opções, memoizada para evitar re-renderizações desnecessárias
  const memoizedRenderDropdownOption = useCallback(
    (option: SelectOption, idx: number, style?: React.CSSProperties) => {
      const isSelected = multiple
        ? Array.isArray(value) && value.includes(option.value)
        : option.value === value;
      const isDisabled =
        option.disabled ||
        (multiple && !isSelected && Array.isArray(value) && value.length >= maxSelected);

      // Sempre retorna um elemento válido, nunca null
      if (typeof renderOption === "function") {
        const rendered = renderOption(option, isSelected);
        return rendered ? (
          <li
            key={option.value}
            id={`option-${option.value}`}
            className={`dropdown-item${isSelected ? " selected" : ""}${highlightedIndex === idx ? " highlighted" : ""}${isDisabled ? " disabled" : ""} ${optionClassName ?? ""}`}
            role="option"
            aria-selected={isSelected}
            aria-disabled={isDisabled}
            tabIndex={-1}
            onClick={() => !isDisabled && handleLimitedOptionSelect(option.value)}
            onMouseEnter={() => setHighlightedIndex(idx)}
            style={style}
          >
            {rendered}
          </li>
        ) : (
          <OptionItem
            key={option.value}
            option={{ ...option, disabled: isDisabled }}
            selected={isSelected}
            highlighted={highlightedIndex === idx}
            onClick={handleLimitedOptionSelect}
            onMouseEnter={() => setHighlightedIndex(idx)}
            optionClassName={optionClassName}
            style={style}
          />
        );
      }
      // Fallback sempre válido
      return (
        <OptionItem
          key={option.value}
          option={{ ...option, disabled: isDisabled }}
          selected={isSelected}
          highlighted={highlightedIndex === idx}
          onClick={handleLimitedOptionSelect}
          onMouseEnter={() => setHighlightedIndex(idx)}
          optionClassName={optionClassName}
          style={style}
        />
      );
    },
    [
      multiple,
      value,
      maxSelected,
      renderOption,
      highlightedIndex,
      optionClassName,
      handleLimitedOptionSelect,
      setHighlightedIndex,
    ]
  );

  // Use a type guard to safely access data-testid without using 'any'
  // Allow data-testid in containerProps via type assertion
  const dataTestId =
    typeof (containerProps as Record<string, unknown>)?.["data-testid"] === "string"
      ? (containerProps as Record<string, unknown>)["data-testid"]
      : "select-field";


  // Fecha ao clicar fora
  useEffect(() => {
    if (!isDropdownOpen) return;
    function handleClickOutside(event: MouseEvent) {
      const wrapperNode = inputRef.current;
      if (
        wrapperNode &&
        event.target instanceof Node &&
        !wrapperNode.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen, inputRef, setIsDropdownOpen]);

  // Fecha dropdown se disabled ou readOnly mudarem para true
  useEffect(() => {
    if ((disabled || readOnly) && isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  }, [disabled, readOnly, isDropdownOpen, setIsDropdownOpen]);

  // NOVO: Suporte a composição via <Option>
  
  if (!props.options && props.children) {
    React.Children.forEach(props.children, child => {
      if (isOptionElement(child)) {
        composedOptions.push({
          value: child.props.value,
          label: typeof child.props.children === "string"
            ? child.props.children
            : child.props.children !== undefined
              ? React.Children.toArray(child.props.children).join("")
              : "",
          disabled: child.props.disabled,
        });
      }
    });
  }

  // Adicione uma classe utilitária para "screen reader only" se não existir
  const srOnly = "sr-only"; // ou defina no seu CSS: .sr-only { ... }

  return (
    <div
      {...containerProps}
      ref={dropdownRef}
      className={clsx(
        "w-full max-w-full relative font-spaceGrotesk",
        theme === "dark" ? "text-[#e3eaff]" : "",
        className
      )}
      tabIndex={tabIndex}
      data-testid={dataTestId}
      role={containerProps?.role ?? "group"}
      aria-labelledby={label ? `label-${selectId}` : undefined}
      aria-describedby={
        [
          helperText
            ? `helper-${selectId}`
            : error || errorMessage
              ? `helper-${selectId}`
              : undefined,
          props.tooltip ? `tooltip-desc-${selectId}` : undefined // <-- Adicione o id do tooltip
        ].filter(Boolean).join(" ") || undefined
      }
      onFocus={e => {
        setIsFocused(true);
        onFocus?.(e);
      }}
      onBlur={e => {
        setIsFocused(false);
        onBlur?.(e);
      }}
    >
      {(label || props.tooltip) && (
        <div className={clsx(
          "flex items-center justify-between",
          compact ? "mb-0 h-5 text-xs" : "mb-1 h-7"
        )}>
          {label && (
            <label
              className={clsx(
                "font-semibold text-[1rem] leading-tight tracking-tight font-inter",
                error
                  ? (theme === "dark" ? "text-[#ff7675]" : "text-[#d13438]")
                  : (theme === "dark" ? "text-[#bbb]" : "text-[#605e5c]")
              )}
              htmlFor={selectId}
              id={`label-${selectId}`}
            >
              {label}
              {required && (
                <span
                  className={clsx(
                    "ml-1",
                    error
                      ? (theme === "dark" ? "text-[#ff7675]" : "text-[#d13438]")
                      : "text-[#d13438]"
                  )}
                >
                  *
                </span>
              )}
            </label>
          )}
          {props.tooltip && (
            <Tooltip
              tooltip={props.tooltip}
              placement="top"
              tabIndex={0}
              style={{ marginLeft: 6 }}
              ariaLive="off"
              data-testid="selectfield-tooltip"
              className={clsx(
                theme === "dark" ? "text-[#bbb]" : "text-[#605e5c]"
              )}
            />
          )}
        </div>
      )}

      {/* Tooltip sempre acessível para leitores de tela */}
      {props.tooltip && (
        <span
          id={`tooltip-desc-${selectId}`}
          className={srOnly}
          aria-live="polite"
          data-testid="selectfield-tooltip-sronly"
        >
          {props.tooltip}
        </span>
      )}

      <div
        className={clsx(
          // Agora as classes de borda/fundo/foco ficam aqui!
          "flex items-center w-full relative rounded-md border transition-colors duration-150",
          compact
            ? "min-h-[28px] px-2 py-0 text-sm"
            : "px-3 py-2 text-base", // Remova min-h e use igual ao InputField
          theme === "dark"
            ? "bg-[var(--select-bg-dark,#23242a)] border-[var(--select-border-dark,#33384d)] focus-within:border-[var(--select-focus-dark,#7b5cff)] focus-within:ring-2 focus-within:ring-[var(--select-focus-dark,#7b5cff)] focus-within:bg-[var(--select-bg-focus-dark,#232c3a)]"
            : "bg-[var(--select-bg,#fff)] border-[var(--select-border,#c8c6c4)] focus-within:border-[var(--select-focus,#0078d4)] focus-within:ring-2 focus-within:ring-[var(--select-focus-ring,#c7e0f4)] focus-within:bg-[var(--select-bg-focus,#f3f9fd)]",
          error && (theme === "dark"
            ? "border-[var(--select-error-dark,#ff7675)] bg-[var(--select-error-bg-dark,#2a232a)] ring-2 ring-[var(--select-error-ring-dark,#ffb6b6)]"
            : "border-[var(--select-error,#d13438)] bg-[var(--select-error-bg,#fdf6f6)] ring-2 ring-[var(--select-error-ring,#f3b6b6)]"),
          showSuccess && (theme === "dark"
            ? "border-[var(--select-success-dark,#00e676)] bg-[var(--select-success-bg-dark,#1a2a1a)] ring-2 ring-[var(--select-success-ring-dark,#00e676)]"
            : "border-[var(--select-success,#107c10)] bg-[var(--select-success-bg,#f6fff6)] ring-2 ring-[var(--select-success-ring,#b7f3b6)]"),
          isFocused && !error && !showSuccess && (theme === "dark"
            ? "border-[var(--select-focus-dark,#7b5cff)] ring-2 ring-[var(--select-focus-dark,#7b5cff)] bg-[var(--select-bg-focus-dark,#232c3a)]"
            : "border-[var(--select-focus,#0078d4)] ring-2 ring-[var(--select-focus-ring,#c7e0f4)] bg-[var(--select-bg-focus,#f3f9fd)]"),
          disabled && (theme === "dark"
            ? "bg-[var(--select-disabled-bg-dark,#181c24)] text-[var(--select-disabled-text-dark,#666)] cursor-not-allowed"
            : "bg-[var(--select-disabled-bg,#f3f2f1)] text-[var(--select-disabled-text,#a19f9d)] cursor-not-allowed"),
          inputClassName
        )}
        onClick={() => {
          if (disabled || readOnly) return;
          if (!isDropdownOpen) setIsDropdownOpen(true);
        }}
        onKeyDown={e => {
          if ((Boolean(disabled) || Boolean(readOnly)) && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
            e.preventDefault();
            return;
          }
          handleWrapperKeyDown(e);
        }}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isDropdownOpen}
        aria-label={ariaLabel}
        aria-controls={isDropdownOpen ? `dropdown-listbox-${selectId}` : undefined}
        aria-owns={isDropdownOpen ? `dropdown-listbox-${selectId}` : undefined}
        aria-activedescendant={
          isDropdownOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]
            ? `option-${filteredOptions[highlightedIndex].value}`
            : undefined
        }
        tabIndex={0}
        ref={inputRef}
        aria-disabled={disabled}
        aria-readonly={readOnly}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-errormessage={error || errorMessage ? `helper-${selectId}` : undefined}
        data-testid="selectfield-combobox"
        aria-describedby={
          [
            helperText || error || errorMessage || props.showSuccess
              ? `helper-${selectId}`
              : undefined,
            props.tooltip ? `tooltip-desc-${selectId}` : undefined
          ].filter(Boolean).join(" ") || undefined
        }
      >
        <span
          className={clsx(
            "flex items-center w-full rounded-md bg-transparent",
            compact
              ? "min-h-[28px] px-2 py-0 text-sm"
              : "px-3 py-2 text-base", // Remova min-h e use igual ao InputField
            inputClassName,
            disabled && "bg-transparent text-[#a19f9d]",
            readOnly && "bg-gray-100 text-[#a19f9d]"
          )}
          data-testid="selectfield-chips"
        >
          {multiple && Array.isArray(value) && value.length > 0 ? (
            <Chips
              items={finalOptions
                .filter(opt => value.includes(opt.value))
                .map(opt => ({
                  id: opt.value,
                  label: opt.label,
                }))}
              onDelete={handleChipDelete}
              onClearAll={handleClearAll}
              theme={theme}
              error={error}
              helperText={helperText}
              successMessage={showSuccess && typeof successMessage === "string" ? successMessage : undefined}
              data-testid="selectfield-chips-list"
            />
          ) : value
            ? selectedLabel
            : <span className={clsx(
                theme === "dark" ? "text-[#d1d5db]" : "text-[#605e5c]"
              )} data-testid="selectfield-placeholder">{placeholder}</span>}
          {showSuccess && successIcon && (
            <span className="ml-2 text-[#107c10]" data-testid="selectfield-success-icon">{successIcon}</span>
          )}
        </span>
        <span
          className={clsx(
            "absolute z-10 flex items-center justify-center opacity-85 cursor-pointer transition-colors",
            compact
              ? "right-2 w-3 h-3 text-xs"
              : "right-3 w-5 h-5 text-base",
            isDropdownOpen && "text-[#2564cf]"
          )}
          onClick={e => {
            e.stopPropagation();
            if (disabled || readOnly) {
              setIsDropdownOpen(false);
              return;
            }
            setIsDropdownOpen(!isDropdownOpen);
          }}
          role="button"
          aria-label={isDropdownOpen ? "Fechar lista" : "Abrir lista"}
          aria-pressed={isDropdownOpen}
          tabIndex={-1}
          data-testid="selectfield-chevron"
        >
          <FaChevronDown size={compact ? 12 : 18} />
        </span>
      </div>
      {isDropdownOpen && !disabled && !readOnly && (
        <>
          {/* Backdrop visual para fechar ao clicar fora */}
          <div
            className="fixed inset-0 z-10 bg-black/10"
            onClick={() => setIsDropdownOpen(false)}
            aria-hidden="true"
            data-testid="selectfield-backdrop"
          />
          <div
            className={clsx(
              "absolute left-0 right-0 top-[calc(100%+4px)] z-20 border rounded-md shadow-lg mt-1 py-1",
              theme === "dark"
                ? "bg-[#23242a] border-[#33384d] text-[#e3eaff]"
                : "bg-white border-[#d1d1d1]",
              dropdownClassName
            )}
            role="presentation"
            data-testid="selectfield-dropdown"
          >
            {searchable && (
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                disabled={disabled}
                searchInputClassName={searchInputClassName}
                searchInputRef={searchInputRef}
                data-testid="selectfield-searchbar"
              />
            )}
            {(
              <>
                {filteredOptions.length === 0 ? (
                  <li
                    data-testid="no-options"
                    className="px-5 py-3 text-base text-gray-400 cursor-not-allowed select-none font-spaceGrotesk"
                  >
                    Nenhuma opção encontrada
                  </li>
                ) : filteredOptions.length > 100 ? (
                  <List
                    height={240}
                    itemCount={filteredOptions.length}
                    itemSize={40}
                    width="100%"
                    outerElementType={DropdownListbox}
                    className="max-h-60 overflow-y-auto list-none p-0 m-0"
                    data-testid="selectfield-virtual-list"
                  >
                    {({ index, style }) =>
                      memoizedRenderDropdownOption(filteredOptions[index], index, style)
                    }
                  </List>
                ) : (
                  <ul
                    className="max-h-44 overflow-y-auto list-none p-0 m-0"
                    role="listbox"
                    id={`dropdown-listbox-${selectId}`}
                    aria-activedescendant={
                      highlightedIndex >= 0 && filteredOptions[highlightedIndex]
                        ? `option-${filteredOptions[highlightedIndex].value}`
                        : undefined
                    }
                    aria-multiselectable={multiple ? "true" : undefined}
                    tabIndex={0}
                    data-testid="selectfield-listbox"
                  >
                    {filteredOptions.map((option, idx) => (
                      <React.Fragment key={option.value}>
                        {memoizedRenderDropdownOption(option, idx)}
                      </React.Fragment>
                    ))}
                  </ul>
                )}
              </>
            )}
            {dropdownFooter && (
              <div
                className="border-t border-gray-200 px-4 py-2 text-sm text-gray-500 font-spaceGrotesk"
                data-testid="dropdown-footer"
              >
                {dropdownFooter}
              </div>
            )}
          </div>
          <select
            ref={nativeSelectRef}
            id={selectId}
            name={name} // Propague o atributo name
            value={value}
            onChange={e => {
              if (multiple) {
                const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                handleOptionSelect(selected[selected.length - 1]);
              } else {
                handleOptionSelect(e.target.value);
              }
            }}
            multiple={multiple}
            onFocus={() => {
              inputRef.current?.focus();
            }}
            className="absolute left-0 top-0 w-full h-full opacity-0 pointer-events-none z-0"
            tabIndex={-1}
            aria-hidden="true"
            disabled={disabled}
            required={required}
            aria-labelledby={label ? `label-${selectId}` : undefined}
          >
            {!multiple && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {finalOptions.map(option => (
              <option key={option.value} value={option.value} disabled={option.disabled} data-testid={`selectfield-option-${option.value}`}>
                {option.label}
              </option>
            ))}
          </select>
        </>
      )}
      {multiple && Array.isArray(value) && value.length > 0 && (
        <button
          type="button"
          className="mt-2 bg-gray-100 text-gray-700 border border-gray-300 rounded px-3 py-1 text-sm hover:bg-gray-200 focus:bg-gray-200 transition"
          onClick={e => {
            e.stopPropagation();
            handleClearAll();
          }}
          aria-label="Limpar seleção"
          tabIndex={0}
          data-testid="selectfield-clear"
        >
          Limpar seleção
        </button>
      )}
      <div className={clsx(
        "mt-1 pl-1",
        compact ? "text-xs mt-0" : "text-sm",
        error
          ? (theme === "dark" ? "text-[#ff7675]" : "text-[#d13438]")
          : (theme === "dark" ? "text-[#d1d5db]" : "text-[#605e5c]"),
        (error || props.showSuccess) && "animate-fadeIn"
      )} id={`helper-${selectId}`} role={error ? "alert" : undefined}>
        {error
          ? resolveMessage(errorMessage, { value: valueString })
          : props.showSuccess
            ? resolveMessage(successMessage, { value: valueString }) ?? i18n.getDefaultSuccessMessage?.()
            : helperText}
      </div>
    </div>
  );
});

SelectField.displayName = "SelectField";
Option.displayName = "Option";
export default React.memo(SelectField, isEqual);